// The "All venues" view: every venue's board fetched side by side, then the
// market-wide picture in the league-table shape rwa.xyz uses (ported from
// radar's market overview). Headline tiles, then value ranked by venue and by
// the chain it sits on (no per-stock league, by request). A venue name switches the
// picker to that venue. One venue failing never blocks the rest: it is listed
// as unavailable and left out of the totals.

import { useEffect, useMemo, useState, type ReactNode } from "react";

import {
  allHeatTokens,
  allStockChainRows,
  chainStockStats,
  stockRowsTotal,
  chainLeague,
  MULTI_CHAIN,
  SUPPLY_TOLERANCE,
  overviewCoverage,
  overviewTotals,
  underlyingLeague,
  venueLeague,
  venuesTileNote,
  type ChainLeagueRow,
  type VenueLeagueRow,
  type VenueLoad,
} from "../all-venues";
import { fetchBoard, NotPublished } from "../api";
import { cleanFlow, flowMethodText, flowText, flowTone } from "../flows";
import { partialNote, partialText, type PartialFigure } from "../partial";
import { ago, compactUsd, count, utcTime } from "../format";
import styles from "./AllVenues.module.css";
import { CrossClass } from "./CrossClass";
import { Heatmap } from "./Heatmap";
import { ScreenPanel, ScreenTabs, useScreenTabsIds } from "./ScreenTabs";
import { StocksTable, type StockRow } from "./StocksTable";

const REFRESH_MS = 60_000;

type Screen = "dashboard" | "stocks" | "heatmap" | "classes";

// All classes first: it is what /tokenization opens on.
const SCREENS = [
  ["classes", "All classes"],
  ["dashboard", "Overview"],
  ["stocks", "All stocks"],
  ["heatmap", "Heatmap"],
] as const;

const pct = (value: number) => `${(value * 100).toFixed(value >= 0.1 ? 1 : 2)}%`;

/** Fetches every venue's board in parallel and, unless told not to, refreshes them every minute. */
export function useAllBoards(venues: ReadonlyArray<{ id: string; label: string }>, refresh = true): VenueLoad[] {
  const [loads, setLoads] = useState<VenueLoad[]>(() =>
    venues.map((v) => ({ id: v.id, label: v.label, status: "loading", board: null })),
  );

  useEffect(() => {
    const controller = new AbortController();
    const update = (id: string, change: (load: VenueLoad) => VenueLoad) =>
      setLoads((current) => current.map((load) => (load.id === id ? change(load) : load)));
    const loadAll = () => {
      for (const venue of venues) {
        fetchBoard(venue.id, controller.signal)
          .then((board) => update(venue.id, (load) => ({ ...load, status: "ready", board, error: undefined })))
          .catch((e: unknown) => {
            if (controller.signal.aborted) return;
            // A failed refresh keeps the last board that loaded.
            update(venue.id, (load) =>
              load.board
                ? load
                : {
                    ...load,
                    status: e instanceof NotPublished ? "pending" : "error",
                    error: e instanceof Error ? e.message : String(e),
                  },
            );
          });
      }
    };
    loadAll();
    const timer = refresh ? setInterval(loadAll, REFRESH_MS) : undefined;
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [venues, refresh]);

  return loads;
}

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <li className="tile">
      <span className="tile-label">{label}</span>
      <span className="tile-value" data-absent={value === "—"}>
        {value}
      </span>
      {note ? <span className="tile-note">{note}</span> : null}
    </li>
  );
}

function ShareBar({ share }: { share: number | null }) {
  if (share === null) return <span className={styles.absent}>—</span>;
  return (
    <span className={styles.share}>
      <span aria-hidden className={styles.shareTrack}>
        <span className={styles.shareFill} style={{ width: `${Math.min(100, share * 100)}%` }} />
      </span>
      {pct(share)}
    </span>
  );
}

function Panel({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <section aria-label={title} className={`panel ${styles.panel}`}>
      <header className="panel-head">
        <h2>{title}</h2>
        <span>{note}</span>
      </header>
      {children}
    </section>
  );
}

const absent = (text: string) => <span className={styles.absent}>{text}</span>;

/** A league cell's coverage, under its figure. */
const CoverageLine = ({ text }: { text: string | null }) => (text ? <span className={styles.coverageLine}>{text}</span> : null);

/** A figure that may be a partial sum, with its coverage or its reason under it. */
function FigureCell({ figure, format }: { figure: PartialFigure; format: (v: number) => string }) {
  const note = partialNote(figure);
  const tone = figure.kind !== "none" ? flowTone(cleanFlow(figure.value)) : null;
  return (
    <>
      {figure.kind === "none" ? absent("—") : <span className={tone ?? undefined}>{partialText(figure, format)}</span>}
      <CoverageLine text={note} />
    </>
  );
}

/** How a venue's flow was measured, plus its mint + burn where our own transfer index reads it. */
function flowTitle(row: VenueLeagueRow): string | undefined {
  const method = flowMethodText(row.flowMethod);
  const mintBurn = row.flowMethod === "transfer_index" && row.mintBurnUsd !== null ? `Minted + burned: ${compactUsd(row.mintBurnUsd)}` : null;
  return [method, mintBurn].filter(Boolean).join(". ") || undefined;
}

const ALL_CHAINS = "all";

/**
 * Every tokenized stock token, one row per chain it has supply on (the user's
 * ask: the tokens themselves, never grouped by the company), with a chain
 * filter, a per-chain breakdown that doubles as the filter, and a count and
 * total for what is shown. The venue filter and search are the grid's own.
 */
function StocksByChain({ rows, onSelectToken }: { rows: StockRow[]; onSelectToken?: (id: string) => void }) {
  const stats = useMemo(() => chainStockStats(rows), [rows]);
  const [chain, setChain] = useState(ALL_CHAINS);
  const [allChips, setAllChips] = useState(false);
  const shown = useMemo(() => (chain === ALL_CHAINS ? rows : rows.filter((r) => r.chainId === chain)), [rows, chain]);
  const current = stats.find((s) => s.chain === chain);
  const totals = useMemo(() => stockRowsTotal(shown), [shown]);
  const tokens = totals.tokens;
  const chips = allChips ? stats : stats.slice(0, 8);
  return (
    <>
      <section aria-label="Stocks by chain" className={`panel ${styles.panel}`}>
        <header className="panel-head">
          <h2>Stocks by chain</h2>
          <span>value and tokens per chain · pick one to filter</span>
        </header>
        <div className={styles.chainChips} role="group" aria-label="Chain">
          <button aria-pressed={chain === ALL_CHAINS} className={styles.chainChip} onClick={() => setChain(ALL_CHAINS)} type="button">
            <span className={styles.chipName}>All chains</span>
            <span className={styles.chipFigure}>{count(new Set(rows.map((r) => r.tokenId ?? r.id)).size)} tokens</span>
          </button>
          {chips.map((s) => (
            <button aria-pressed={chain === s.chain} className={styles.chainChip} key={s.chain} onClick={() => setChain(s.chain)} type="button">
              <span className={styles.chipName}>{s.label}</span>
              <span className={styles.chipFigure}>
                {s.valueUsd > 0 ? compactUsd(s.valueUsd) : s.upperUsd > 0 ? `≤ ${compactUsd(s.upperUsd)}` : "—"}
                {s.valueUsd > 0 && s.upperUsd > 0 ? ` + ≤ ${compactUsd(s.upperUsd)}` : ""} · {count(s.tokens)}
              </span>
            </button>
          ))}
          {stats.length > 8 ? (
            <button className={styles.chainChip} onClick={() => setAllChips(!allChips)} type="button">
              <span className={styles.chipName}>{allChips ? "Fewer" : `All ${stats.length} chains`}</span>
            </button>
          ) : null}
        </div>
        <p className={styles.footnote}>
          <strong>{count(tokens)}</strong> {tokens === 1 ? "token" : "tokens"}
          {chain === ALL_CHAINS ? (
            <>
              {" "}· <strong>{compactUsd(totals.exact + totals.unsplit)}</strong>. Each token is listed once per chain it is on; the total counts
              each token once.
            </>
          ) : (
            <>
              {" "}on {current?.label ?? chain} · <strong>{compactUsd(totals.exact)}</strong>
              {totals.unsplit > 0
                ? `, plus tokens worth ${compactUsd(totals.unsplit)} in all whose chain split is unknown (their chain supplies include bridged copies, as xStocks' do)`
                : ""}
              .
            </>
          )}
        </p>
      </section>
      <StocksTable
        csvName={`all-stocks-${chain === ALL_CHAINS ? "every-chain" : chain}.csv`}
        onSelect={onSelectToken ? (row) => onSelectToken(row.tokenId ?? row.id) : undefined}
        rowNoun="token-chain rows"
        rows={shown}
        venueColumn
      />
    </>
  );
}

function statusText(status: VenueLeagueRow["status"]): string {
  if (status === "loading") return "loading…";
  if (status === "pending") return "first collection in progress";
  return "board didn't load (retrying every minute)";
}

function VenueTable({ rows, onSelectVenue }: { rows: VenueLeagueRow[]; onSelectVenue: (id: string) => void }) {
  return (
    <div className={styles.scroll} role="region" aria-label="Venue league" tabIndex={0}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Venue</th>
            <th scope="col" className={styles.num}>Market cap</th>
            <th scope="col">Share</th>
            <th scope="col" className={styles.num}>Tokens</th>
            <th scope="col" className={styles.num}>Priced</th>
            <th scope="col" className={styles.num}>Net flow · 24h</th>
            <th scope="col">Chains</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id} data-unavailable={row.status !== "ready"}>
              <td className={styles.rank}>{row.valueUsd === null ? "" : index + 1}</td>
              <td>
                <button className={styles.venueLink} onClick={() => {
                    onSelectVenue(row.id);
                    window.scrollTo({ top: 0 });
                  }} title={`Open ${row.label}`} type="button">
                  {row.label}
                </button>
              </td>
              {row.status !== "ready" ? (
                <td className={styles.absent} colSpan={6}>
                  {statusText(row.status)}
                </td>
              ) : (
                <>
                  <td className={styles.num}>{row.valueUsd === null ? absent("no priced tokens") : compactUsd(row.valueUsd)}</td>
                  <td>
                    <ShareBar share={row.share} />
                  </td>
                  <td className={styles.num}>{row.tokens === null ? absent("—") : count(row.tokens)}</td>
                  <td className={styles.num}>{row.priced === null ? absent("—") : count(row.priced)}</td>
                  <td className={styles.num} title={flowTitle(row)}>
                    <FigureCell figure={row.flowFigure} format={flowText} />
                  </td>
                  <td className={styles.muted}>{row.chains.length ? row.chains.join(", ") : absent("—")}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChainTable({ rows }: { rows: ChainLeagueRow[] }) {
  if (!rows.length) return <p className="panel-empty">Not enough data yet.</p>;
  const multi = rows.find((row) => row.chain === MULTI_CHAIN);
  return (
    <>
      <div className={styles.scroll} role="region" aria-label="Chain league" tabIndex={0}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Chain</th>
              <th scope="col" className={styles.num}>Value</th>
              <th scope="col">Share</th>
              <th scope="col" className={styles.num}>Tokens</th>
              <th scope="col">Venues</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.chain}>
                {/* The unsplit bucket is not a chain, so it takes no rank. */}
                <td className={styles.rank}>{row.chain === MULTI_CHAIN ? "" : index + 1}</td>
                <td className={styles.strong}>
                  {row.label}
                  {row.chain === MULTI_CHAIN ? <sup aria-hidden>*</sup> : null}
                </td>
                <td className={styles.num}>{compactUsd(row.valueUsd)}</td>
                <td>
                  <ShareBar share={row.share} />
                </td>
                <td className={styles.num}>{count(row.tokens)}</td>
                <td className={styles.muted}>{row.venues.join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {multi ? (
        <p className={styles.footnote}>
          * Multi-chain (not split): {count(multi.tokens)} {multi.tokens === 1 ? "token" : "tokens"} (
          {multi.venues.join(", ")}) whose per-chain supplies exceed circulating supply by more than{" "}
          {Math.round(SUPPLY_TOLERANCE * 100)}%, because bridged and inventory copies are counted on each chain. Splitting
          them by supply would overstate some chains, so their {compactUsd(multi.valueUsd)} is kept whole here. Every other
          token is split by its per-chain supply.
        </p>
      ) : null}
    </>
  );
}

export function AllVenues({
  venues,
  screen,
  onScreen,
  onSelectVenue,
  onSelectToken,
}: {
  venues: ReadonlyArray<{ id: string; label: string }>;
  /** From ?screen=; null opens the default, All classes. */
  screen: string | null;
  onScreen: (next: Screen) => void;
  onSelectVenue: (id: string) => void;
  /** Opens a token (by API id) from the all-stocks grid. */
  onSelectToken?: (id: string) => void;
}) {
  const loads = useAllBoards(venues);
  const tabIds = useScreenTabsIds();
  const league = useMemo(() => venueLeague(loads), [loads]);
  const chains = useMemo(() => chainLeague(loads), [loads]);
  const stocks = useMemo(() => underlyingLeague(loads), [loads]);
  const totals = useMemo(() => overviewTotals(loads), [loads]);
  const rows = useMemo(() => allStockChainRows(loads), [loads]);

  const ready = loads.filter((load) => load.board);
  const waiting = loads.filter((load) => !load.board && load.status === "loading");
  const missing = loads.filter((load) => !load.board && load.status !== "loading");
  const latest = ready.reduce<string | null>((max, load) => {
    const at = load.board?.as_of ?? null;
    return at && (!max || at > max) ? at : max;
  }, null);
  const generated = ready.reduce<string | null>((max, load) => {
    const at = load.board?.generated_at ?? null;
    return at && (!max || at > max) ? at : max;
  }, null);
  // No ?screen= opens on All classes; ?screen=dashboard (old links) still opens the stocks overview.
  const current: Screen = screen === "stocks" || screen === "heatmap" || screen === "dashboard" ? screen : "classes";
  // Built only while the heatmap is open; every venue's tokens, venue-tagged.
  const heatTokens = useMemo(() => (current === "heatmap" ? allHeatTokens(loads) : []), [current, loads]);

  return (
    <>
      <p className="venue-meta">
        {ready.length ? (
          <>
            <strong>{totals.tokens}</strong> tokens across <strong>{totals.venues}</strong> of {totals.venuesListed} venues
            {latest ? (
              <>
                {" "}· latest as of <strong>{utcTime(latest)}</strong>
              </>
            ) : null}
            {generated ? <> · updated {ago(generated)}</> : null} · refreshes every 30 min
          </>
        ) : missing.length === loads.length ? (
          <span className="error">Data unavailable right now: no venue board answered.</span>
        ) : (
          "Loading…"
        )}
      </p>
      {missing.length || (waiting.length && ready.length) ? (
        <p className={styles.coverage}>
          {waiting.length && ready.length ? `Still reading ${waiting.map((v) => v.label).join(", ")}; totals grow as each board lands. ` : ""}
          {missing.length
            ? `${missing.map((v) => v.label).join(", ")} ${missing.length === 1 ? "is" : "are"} unavailable and left out of the totals.`
            : ""}
        </p>
      ) : null}

      <ScreenTabs current={current} ids={tabIds} label="All venues screen" onSelect={onScreen} tabs={SCREENS} />

      <ScreenPanel current={current} ids={tabIds}>
        {current === "classes" ? (
          <CrossClass venues={loads} />
        ) : current === "stocks" ? (
          ready.length ? (
            <StocksByChain onSelectToken={onSelectToken} rows={rows} />
          ) : null
        ) : current === "heatmap" ? (
          ready.length ? (
            <Heatmap scope="any venue" tokens={heatTokens} />
          ) : null
        ) : (
          <>
            <ul className="tiles">
              <Tile label="Tokenized market cap" value={compactUsd(totals.marketCap)} note={`all ${totals.venues} venues with data, summed`} />
              <Tile
                label="Tokens · priced / listed"
                value={ready.length ? `${count(totals.priced)} / ${count(totals.tokens)}` : "—"}
                note={`${count(stocks.length)} underlying stocks`}
              />
              <Tile
                label="Venues"
                value={ready.length ? count(totals.venues) : "—"}
                note={venuesTileNote(loads)}
              />
              <Tile label="Chains" value={ready.length ? count(totals.chains) : "—"} note="carrying priced value" />
              <Tile
                label="Net flow · 24h"
                value={partialText(totals.netFlow, flowText)}
                note={partialNote(totals.netFlow) ?? "units issued minus redeemed, every venue"}
              />
            </ul>

            <Panel title="Venue league" note="ranked by tokenized market cap · click a venue to open it">
              <VenueTable onSelectVenue={onSelectVenue} rows={league} />
            </Panel>

            <Panel title="Chain league" note="value by chain, split by supply share where supplies reconcile">
              <ChainTable rows={chains} />
            </Panel>
          </>
        )}
      </ScreenPanel>
    </>
  );
}
